import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ToastContainer from '../components/common/ToastContainer';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { extractApiErrorMessage } from '../utils/apiErrorUtils';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [dialog, setDialog] = useState({ isOpen: false, options: {} });
    const lastValidationToastRef = useRef({ key: '', at: 0 });

    // Toast Logic
    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const showToast = useCallback((message, type = 'info', title = '') => {
        const id = Date.now().toString();
        const normalizedMessage = type === 'error'
            ? extractApiErrorMessage({ response: { data: message } }, 'Não foi possível concluir a operação. Tente novamente.')
            : String(message || '').trim() || 'Operação concluída.';

        // Auto title if not provided
        if (!title) {
            if (type === 'success') title = 'Sucesso';
            if (type === 'error') title = 'Erro';
            if (type === 'warning') title = 'Atenção';
            if (type === 'info') title = 'Informação';
        }

        setToasts((prev) => [...prev, { id, message: normalizedMessage, type, title }]);

        // Auto remove after 5 seconds
        setTimeout(() => {
            removeToast(id);
        }, 5000);
    }, [removeToast]);

    useEffect(() => {
        const escapeCssSelector = (value) => {
            if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
                return window.CSS.escape(value);
            }
            return String(value).replace(/"/g, '\\"');
        };

        const resolveFieldLabel = (field) => {
            if (!field) return '';
            const ariaLabel = field.getAttribute?.('aria-label');
            if (ariaLabel) return ariaLabel;
            const id = field.getAttribute?.('id');
            if (!id) return '';
            const labelEl = document.querySelector(`label[for="${escapeCssSelector(id)}"]`);
            return labelEl?.textContent?.replace('*', '').trim() || '';
        };

        const handleInvalid = (event) => {
            const field = event.target;
            if (!field || typeof field.validationMessage !== 'string') return;

            const baseMessage = String(field.validationMessage || '').trim();
            if (!baseMessage) return;

            const label = resolveFieldLabel(field);
            const customMessage = field.getAttribute?.('data-validation-message');
            const message = customMessage || (label ? `${label}: ${baseMessage}` : baseMessage);
            const dedupeKey = `${field.name || field.id || 'field'}::${message}`;
            const now = Date.now();

            if (
                lastValidationToastRef.current.key === dedupeKey &&
                now - lastValidationToastRef.current.at < 1200
            ) {
                return;
            }

            lastValidationToastRef.current = { key: dedupeKey, at: now };
            showToast(message, 'warning');
        };

        document.addEventListener('invalid', handleInvalid, true);
        return () => {
            document.removeEventListener('invalid', handleInvalid, true);
        };
    }, [showToast]);

    // Refined Dialog Logic for Promise support
    const [dialogResolver, setDialogResolver] = useState(null);

    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            setDialogResolver(() => resolve); // Store the resolve function
            setDialog({
                isOpen: true,
                options: {
                    type: 'question',
                    confirmText: 'Confirmar',
                    cancelText: 'Cancelar',
                    ...options
                }
            });
        });
    }, []);

    const handleConfirm = () => {
        if (dialogResolver) dialogResolver(true);
        setDialog({ isOpen: false, options: {} });
        setDialogResolver(null);
    };

    const handleCancel = () => {
        if (dialogResolver) dialogResolver(false);
        setDialog({ isOpen: false, options: {} });
        setDialogResolver(null);
    };

    return (
        <NotificationContext.Provider value={{ showToast, removeToast, showConfirm: confirm }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            <ConfirmDialog 
                isOpen={dialog.isOpen} 
                onClose={handleCancel} 
                options={{
                    ...dialog.options,
                    onConfirm: handleConfirm
                }} 
            />
        </NotificationContext.Provider>
    );
};
